<template>
    <v-layout row>
        <v-flex xs12>
            <v-card class="elavation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn icon dark title="Back to main menu" @click="$router.go(-1)">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn v-if="hasDeleteRights && hasPendingUpload" small dark color="error" @click="showDeleteConfirm" title="Delete Upload" :loading="loading_delete"> 
                        <v-icon small dark>delete</v-icon>&nbsp;Delete&nbsp;
                    </v-btn>
                    <v-btn v-if="hasEditRights && hasPendingValidUpload" small dark color="primary" @click="showSaveConfirm" title="Save Valid Upload">
                        <v-icon small dark>save</v-icon>&nbsp;Save&nbsp;
                    </v-btn>
                </v-toolbar>
            </v-card> 
            <v-alert v-if="error" :value="true" color="error" icon="error" outline>
                {{errorMessage}}
            </v-alert>
            <v-layout v-if="!loading && hasPendingUpload" row wrap>
                <v-flex xs12 sm12>
                    <v-card class="elavation-1 mt-5">
                        <v-toolbar flat height="45px">
                            <span class="font-weight-bold ">Upload Summary</span>
                            <v-spacer></v-spacer>
                        </v-toolbar>
                        <v-layout row wrap>
                            <v-flex xs12 sm6>
                                <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'">
                                    <v-flex xs4 class="text-xs-left">
                                        <b>Filename :</b>
                                    </v-flex>
                                    <v-flex xs4 class="text-xs-left">
                                        {{ upload_data.fileName }}
                                    </v-flex>
                                </v-layout>
                                <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'" style="margin-bottom: 10px;">
                                    <v-flex xs4 class="text-xs-left">
                                        <b>Total records :</b>
                                    </v-flex>
                                    <v-flex xs4 class="text-xs-left">
                                        {{ upload_data.totalRecords }}
                                    </v-flex>
                                </v-layout>
                            </v-flex>
                            <v-flex xs12 sm6>
                                <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'">
                                    <v-flex xs4 class="text-xs-left">
                                        <b>Uploaded By :</b>
                                    </v-flex>
                                    <v-flex xs4 class="text-xs-left">
                                        <v-chip style="margin:-5px 0px 0px -5px;">
                                            <v-avatar>
                                                <v-img ref="avatar" :src="upload_data.uploadedBy.avatar"></v-img>
                                            </v-avatar>
                                            {{upload_data.uploadedBy.firstName}} {{upload_data.uploadedBy.lastName}}
                                        </v-chip>
                                    </v-flex>
                                </v-layout>
                                <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'" style="margin-bottom: 10px;">
                                    <v-flex xs4 class="text-xs-left">
                                        <b>Uploaded On :</b>
                                    </v-flex>
                                    <v-flex xs4 class="text-xs-left">
                                        {{ upload_data.uploadedOn | moment('MMM DD, YYYY h:mm A') }}
                                    </v-flex>
                                </v-layout>
                            </v-flex>
                        </v-layout>
                    </v-card>
                </v-flex>
                <v-flex v-if="hasPendingValidUpload" xs12 sm12>
                    <v-card class="elavation-1 mt-5">
                        <v-toolbar flat height="45px">
                            <span class="font-weight-bold green--text">Valid Records</span>
                            <v-spacer></v-spacer>
                        </v-toolbar>
                        <v-data-table
                            :headers="headers"
                            :items="pending_valid_upload"
                            :loading="loading"
                        >  
                            <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>
                            <template v-slot:items="props">
                                <tr title="Click to view/edit details">
                                    <td>{{props.item.email}}</td>
                                    <td>{{props.item.firstName}}</td>
                                    <td>{{props.item.lastName}}</td>
                                    <td>{{props.item.validationMessage}}</td>
                                </tr>
                            </template>
                        </v-data-table>
                    </v-card>
                </v-flex>
                <v-flex v-if="hasPendingInValidUpload" xs12 sm12>
                    <v-card class="elavation-1 mt-5">
                        <v-toolbar flat height="45px">
                            <span class="font-weight-bold red--text">Invalid Records</span>
                            <v-spacer></v-spacer>
                        </v-toolbar>
                        <v-data-table
                            :headers="headers"
                            :items="pending_invalid_upload"
                            :loading="loading"
                        >  
                            <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>
                            <template v-slot:items="props">
                                <tr title="Click to view/edit details">
                                    <td>{{props.item.email}}</td>
                                    <td>{{props.item.firstName}}</td>
                                    <td>{{props.item.lastName}}</td>
                                    <td>{{props.item.validationMessage}}</td>
                                </tr>
                            </template>
                        </v-data-table>
                    </v-card>
                </v-flex>
            </v-layout>
            <v-layout v-if="!loading && !hasPendingUpload" row wrap>
                <v-flex xs12 sm12>
                    <v-alert :value="true" color="info" outline>
                        To upload multiple employees at once, create an Excel file without headers, where each row represents one employee. You can enter multiple employee records in the file.
                        <ul>
                            <li>Column A: Email Address</li>
                            <li>Column B: First Name</li>
                            <li>Column C: Last Name</li>
                        </ul>
                        After uploading, the system will validate the file and let you review the entries before finalizing.
                    </v-alert>
                    <div class="text-xs-center" style="margin-bottom:15px;">
                        <v-progress-linear v-if="uploading" color="primary" indeterminate style="margin-bottom:-3px;" height="3"></v-progress-linear>
                        <vue-dropzone ref="myVueDropzone" 
                            style="height:220px;padding-top:0px; background-color:#F2F0EF;"
                            id="dropzone" 
                            :options="dropzoneOptions"
                            v-on:vdropzone-success="uploadSuccess"
                            v-on:vdropzone-file-added="fileAdded"
                            ></vue-dropzone>
                    </div>
                </v-flex>
            </v-layout> 
        </v-flex>

        <v-dialog v-model="dialogOkCancel" persistent width="600">
            <ok-cancel :msg="dialogOkCancelMsg" alignLeft="true" :dialogOkCancel.sync="dialogOkCancel" @ok="handleOkCancelAction"></ok-cancel>
        </v-dialog>
        <v-dialog v-model="dialogOkOnly" persistent width="600">
            <ok-only :msg="dialogOkOnlyMsg" :dialogOkOnly.sync="dialogOkOnly" @ok="handleOkOnly"></ok-only>
        </v-dialog>

        <v-dialog v-model="dialogSaving" persistent width="600">
            <v-card class="elavation-1" style="border-radius:4px;"> 
                <v-toolbar flat height="45px">
                    <span class="font-weight-bold">Saving Upload</span>
                    <v-spacer></v-spacer>
                </v-toolbar>
                <v-progress-linear color="primary" indeterminate height="3" style="margin-top:-3px;"></v-progress-linear>
                <v-card-text class="text-xs-center">
                    <p class="msg-text">
                        Saving uploaded data...<br>
                        This may take a few moments.<br>
                        Please do not close or refresh the page.
                    </p>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-layout>  
</template>
<script>

import axios from 'axios'
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import pageKeys from '@/common/pagekeys'
import employeesApi from '@/api/employees'
import { hasEditRights, hasDeleteRights } from '@/helpers/access-permissions'

export default {
    name: 'EmployeesUpload',
    metaInfo: {
        title: 'Employees Upload',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Batch upload employees' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
            ]
    },
    components: {
        vueDropzone: vue2Dropzone,
        'ok-cancel': ()=> import("@/components/BasicPopups/OkCancel"),
        'ok-only': ()=> import("@/components/BasicPopups/OkOnly"),
    },
    data () {
        return {        
            error: false,
            errorMessage: '',
            loading: false,
            disabled: false,
            dialogOkCancel: false,
            dialogSaving: false,
            dialogOkOnly: false,
            uploading: false,
            loading_delete: false,
            dialogOkCancelMsg: '',
            dialogOkOnlyMsg: '',
            dialogOkCancelAction: '',
            headers: [
                    {text:'Email', align: 'left',value:'email', width:'15%', sortable:true},
                    {text:'First Name', align: 'left',value:'firstName', width:'10%', sortable:true},
                    {text:'Last Name', align: 'left',value:'lastName', width:'10%', sortable:true},
                    {text:'Validation', align: 'left',value:'validationMessage', width:'10%', sortable:true},
            ],
            dropzoneOptions: {                    
                url: `${axios.defaults.baseURL}Employees/upload`,    
                headers: { 
                    "Authorization": `Bearer ${localStorage.token}`,
                    "page_key": pageKeys.employees,
                    "session_id": localStorage.sessionId,
                    "source": "Customer"
                },
                addRemoveLinks: true,
                dictDefaultMessage: "<br><b class='headline'><u>Drag your excel file or click here to browse</u><b><br><br><p class='subheading'>Maximum 5MB per file.<br>Accepted files types: excel (*.xlsx, *.xls)</p>",
                maxFiles: 1,
                acceptedFiles: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel',
                maxFilesize: 5,   //5mb
            },
            pending_valid_upload: [],
            pending_invalid_upload: [],
            upload_data: null,
        }
    },
    methods: {
        handleOkCancelAction() {
            if (this.dialogOkCancelAction == 'delete') {
                this.deleteUpload()
            }else{
                this.saveUpload()
            }
        },
        showDeleteConfirm() {
            this.dialogOkCancelAction = 'delete'
            this.dialogOkCancelMsg = "This action will <b class='red--text'>DELETE</b> all records uploaded from the Excel file.<br><br><b>Would you like to continue?</b>"
            this.dialogOkCancel = true
        },
        showSaveConfirm() {
            this.dialogOkCancelAction = 'save'
            this.dialogOkCancelMsg = "You're about to <b class='green--text'>SAVE</b> all valid records from the uploaded Excel file.<br>Once saved, employees will receive an email with their login credentials.<br><br><b>Would you like to continue?</b>"
            this.dialogOkCancel = true
        },
        showSaveComplete() {
            this.dialogOkOnlyMsg = "All valid records have been successfully saved.<br>Employees will receive an email with their login credentials shortly."
            this.dialogOkOnly = true
        },
        handleOkOnly() {
            this.$router.push(`/hr/employees`)
        },
        async deleteUpload() {
            try {
                this.loading_delete = true
                this.dialogOkCancel = false
                const response = await employeesApi.deleteUpload(this.upload_data.userUploadId)
                if (response && response.status == 200) {
                    await this.getPreviousUpload()
                }
                this.loading_delete = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async saveUpload() {
            try {
                this.dialogOkCancel = false
                this.dialogSaving = true
                const response = await employeesApi.saveUpload(this.upload_data.userUploadId)
                if (response && response.status == 200) {
                    this.dialogSaving = false
                    this.showSaveComplete()
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async getPreviousUpload(){
            try {
                this.loading = true
                //check if there were previously uploaded that are not saved/cleared yet
                const response = await employeesApi.getUpload()                
                if (response && response.status == 200) {
                    this.pending_valid_upload = response.data.details.filter(x => x.isValid)
                    this.pending_invalid_upload = response.data.details.filter(x => !x.isValid)
                    this.upload_data = response.data
                }else{
                    this.pending_valid_upload = []
                    this.pending_invalid_upload = []
                    this.upload_data = null
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        fileAdded(file) {
            this.uploading = true
        },
        uploadSuccess(file, response) {
            this.uploading = false
            this.getPreviousUpload()
        },
    },
    created() {
        this.getPreviousUpload()

        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: '',
            headerPageTitle: 'Employees Upload'
        })
    },
    computed: {
        isMobile() {
            return window.innerWidth < 600
        },
        hasDeleteRights() {
            return hasDeleteRights(this.$router.currentRoute.path)
        },
        hasEditRights() {
            return hasEditRights(this.$router.currentRoute.path)
        },
        hasPendingValidUpload() {
            return this.pending_valid_upload.length
        },
        hasPendingInValidUpload() {
            return this.pending_invalid_upload.length
        },
        hasPendingUpload() {
            return this.hasPendingValidUpload || this.hasPendingInValidUpload
        }
    }
}
</script>
<style scoped>
    .section-layout {
        padding:0px 50px 0px 50px;
    }
    .section-layout-mobile {
        padding:0px;
    }
</style>